import React, { useCallback, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";
import { Text, COLOR_SYSTEM } from "components/atoms";
import { AIReviewStatus, PromptType } from "common/models/types";
import ReviewSection from "components/templates/AIReviewDetailTemplate/ReviewSection";
import { buildHospitalAliasMapFromRecord } from "utils/extractDepartment";
import { collectSnapDrugs, pillsNameFallback } from "utils/drugImageUtils";
import PatientInfoDisplay from "components/molecules/PatientInfoDisplay";
import PrintSidePanel from "components/templates/AIReviewPrintTemplate/PrintSidePanel";
import type { PrintOptions } from "components/templates/AIReviewPrintTemplate";
import PrescriptionScopeSection from "components/templates/AIReviewPrintTemplate/PrescriptionScopeSection";
import {
  drugCardSizeCss,
  DrugCardSize,
} from "components/templates/AIReviewPrintTemplate/drugCardSizeCss";
import {
  ChangePill,
  useChangeNotice,
} from "components/templates/AIReviewPrintTemplate/changeNotice";
import {
  computeOptionAvailability,
  extractMentionedItemCodes,
  getMentionedAdditions,
} from "utils/prescriptionPrintScope";
import { checkSectionExistence } from "utils/sectionParser";
import { fetchEmbedReport } from "services/caringnoteApi";
import { useCaringNoteReport } from "./useCaringNoteReport";
import CaringNoteStatus from "./components/CaringNoteStatus";

/**
 * 탑재뷰(embed) — `/caringnote/embed/:token` (계획 CN-13 · T3).
 *
 * 케어링노트 상담 화면 iframe 안에서 **로그인 없이** 복약지도문 + 접이식 출력 설정 패널을 보여준다.
 * - iframe 뷰포트를 꽉 채우고 본문만 내부 스크롤(§3-4). @media print에서 스크롤 컨테이너를
 *   auto/visible로 재설정하고 패널을 숨겨 전체 본문만 인쇄되게 한다.
 * - 쓰기 UI(재시도·피드백) 없음. 어떤 오류에도 로그인 화면으로 보내지 않는다.
 * - 본문은 **환자 정보 → 처방 정보 → 분석 결과** 순. 앞의 둘은 `patientSnap`이 있고 옵션이 켜졌을 때만.
 * - 출력 설정은 약사 화면의 `PrintSidePanel`을 **접이식 모드**로 재사용한다. 단 약사 화면의
 *   `print_options` localStorage 키는 읽지도 쓰지도 않는다 — 전용 키(펼침 여부)만 쓰고 옵션 값은
 *   매 진입 시 기본값(전부 숨김)으로 시작한다(§5.4).
 */

// 탑재뷰 전용 저장 키 — 약사 화면의 `print_options`와 절대 공유하지 않는다(§5.4). 펼침 여부만 저장한다.
const PANEL_OPEN_KEY = "caringnote_embed_panel_open";

// localStorage 접근 전체를 try/catch로 감싼다 — 크로스사이트 iframe 차단 환경에서 getItem이 SecurityError를
// 던진다. 실패 시 접힘 기본값(false)으로 조용히 떨어지고 화면은 정상 렌더한다(§5.4 · AC-48).
function readPanelOpen(): boolean {
  try {
    return localStorage.getItem(PANEL_OPEN_KEY) === "true";
  } catch {
    return false;
  }
}
function persistPanelOpen(open: boolean): void {
  try {
    localStorage.setItem(PANEL_OPEN_KEY, String(open));
  } catch {
    /* 저장 실패는 무시 — 다음 진입 시 기본값(접힘)으로 시작한다. */
  }
}

// 폭 판정 상수(§5.3). 셸 폭에서 이만큼 빼서 "펼침 시 본문에 남는 폭"을 구하고 표 최소폭과 비교한다.
const PANEL_WIDTH = 240;
const RAIL_WIDTH = 48;
const BODY_PADDING = 40; // ScrollBody padding 20 × 2
const HYSTERESIS = 24;
const TABLE_MIN_NO_IMAGES = 350;
const TABLE_MIN_WITH_IMAGES = 470;

// 첫 진입 옵션 — 전부 숨김. 값은 저장하지 않으므로 매 진입 시 여기서 시작한다(§5.4).
// referenceMode는 공유 PrintOptions의 필수 필드라 반드시 포함(탑재뷰는 항상 'none').
// 복용약 범위·표시 옵션은 약사 인쇄 화면과 같은 진입 기본값을 쓴다 — 범위는 항상 '전체'로 시작하고
// (비저장 정책과 동일), 종료 예상 배지는 켬. 카드 크기만 '보통'(성상 13px) — 파트너 성상·각인
// 스펙의 인쇄 규격(D-17: 성상 13px)과 같은 단계라 탑재뷰 기존 인쇄물이 그대로 유지된다.
const DEFAULT_OPTIONS: PrintOptions = {
  showPatientInfo: false,
  showPrescriptionTable: false,
  prescriptionViewMode: "date",
  referenceMode: "none",
  showDrugImages: false,
  hidePharmacistReport: false,
  hidePatientGuide: false,
  drugScope: "all",
  drugScopeAddMentioned: false,
  drugScopeMentionedStyle: "drug",
  markEndedDrugs: true,
  patientGuideCardSize: "medium",
  classificationDisplay: "none",
};

// @page는 문서 레벨 at-rule이라 styled.div 스코프에 넣으면 동작하지 않는다 → createGlobalStyle.
const EmbedPrintStyle = createGlobalStyle`
  @media print {
    @page {
      size: A4;
      margin: 20mm;
    }

    .cn-embed-print,
    .cn-embed-print * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
      overflow: visible !important;
    }
  }
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #fff;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};

  @media print {
    height: auto;
  }
`;

const Header = styled.div<{ $reserveRight?: number }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[150]};
  /* 본문과 동일한 우측 예약폭 — push에서 헤더 책임 안내 문구가 패널 밑으로 잘리지 않게 한다(계약 §6). */
  margin-right: ${(props) => props.$reserveRight ?? 0}px;
  transition: margin-right 0.2s;

  @media print {
    display: none;
  }
`;

const Notice = styled.div`
  flex: 1;
  min-width: 0;
`;

// 가장자리 알림 알약을 스크롤과 무관하게 고정하기 위한 기준 — 스크롤 컨테이너(ScrollBody)를 감싼다.
const BodyWrap = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  @media print {
    display: block;
  }
`;

const ScrollBody = styled.div<{ $reserveRight?: number }>`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  /* 우측 도킹 패널/레일이 차지하는 폭만큼 본문을 밀어준다(push). overlay면 레일 폭(48)만 예약해
     펼쳐도 본문 폭이 변하지 않는다(§5.3 · AC-22·25). */
  margin-right: ${(props) => props.$reserveRight ?? 0}px;
  transition: margin-right 0.2s;

  /* 인쇄 시 스크롤 뷰포트만 인쇄되는 것을 방지하고, 패널 예약폭을 없애 본문이 전체 폭을 쓴다(AC-50·54). */
  @media print {
    flex: none;
    height: auto;
    overflow: visible;
    padding: 0;
    margin-right: 0;
  }
`;

const Inner = styled.div`
  max-width: 820px;
  margin: 0 auto;
  word-break: break-word;

  th,
  td {
    word-break: break-word;
  }
`;

// 섹션 래퍼 — 켜진 각 섹션은 인쇄 시 새 페이지에서 시작한다(첫 가시 섹션 제외, AC-52).
const Section = styled.div<{ $pageBreak?: boolean }>`
  margin-bottom: 32px;

  @media print {
    ${(props) =>
      props.$pageBreak &&
      `
      page-break-before: always;
      break-before: page;
    `}
  }
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
`;

// 본문 의약품 카드(사진·성상·각인) 크기 — 약사 인쇄 화면과 같은 규칙(환자용 복약지도문 하위 옵션).
const AnalysisCardScope = styled.div<{ $cardSize: DrugCardSize }>`
  ${({ $cardSize }) => drugCardSizeCss($cardSize)}
`;

const noop = () => {};

const CaringNoteEmbedPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { state, retry } = useCaringNoteReport(fetchEmbedReport, token);

  // ── 훅은 조기 반환보다 위에서 무조건 호출한다(React 규칙) ──
  const [printOptions, setPrintOptions] = useState<PrintOptions>(DEFAULT_OPTIONS);
  const initialOpen = useMemo(() => readPanelOpen(), []);
  const [panelOpen, setPanelOpen] = useState<boolean>(initialOpen);
  // push/overlay 모드. 기본 overlay(안전 — ResizeObserver 부재 환경과 동일). 셸 관측 후 갱신된다.
  const [mode, setMode] = useState<"push" | "overlay">("overlay");

  const showDrugImages = printOptions.showDrugImages;
  const roRef = useRef<ResizeObserver | null>(null);

  // 가장자리 알림 — 옵션 변경이 본문 뷰포트 밖 섹션에 반영됐을 때만 잠깐 표시 (약사 인쇄 화면과 공유 훅)
  const {
    containerRef: bodyRef,
    changePill,
    recordChange,
    handlePillClick,
  } = useChangeNotice(printOptions);

  const handleOptionChange = (option: keyof PrintOptions, value: any) => {
    // 변경이 반영될 섹션을 기록 — 렌더 후 뷰포트 밖이면 가장자리 알림을 띄운다
    recordChange(option);
    setPrintOptions((prev) => ({ ...prev, [option]: value }));
  };

  const handleToggle = (open: boolean) => {
    setPanelOpen(open);
    persistPanelOpen(open);
  };

  // 외곽 셸(Root)을 ResizeObserver로 관측해 push/overlay를 판정한다. 본문이 아니라 셸을 관측해야
  // push→폭감소→overlay→폭복귀 되먹임이 안 생긴다(§5.3). 부재 환경(jsdom·구형)에서는 overlay 고정.
  const setShellRef = useCallback(
    (node: HTMLDivElement | null) => {
      // 콜백 ref가 다시 불릴 때(showDrugImages 변경/언마운트) 이전 observer를 먼저 끊는다.
      roRef.current?.disconnect();
      roRef.current = null;
      if (!node || typeof ResizeObserver === "undefined") {
        setMode("overlay");
        return;
      }
      const tableMin = showDrugImages ? TABLE_MIN_WITH_IMAGES : TABLE_MIN_NO_IMAGES;
      const decide = (w: number, prev: "push" | "overlay"): "push" | "overlay" => {
        const available = w - PANEL_WIDTH - RAIL_WIDTH - BODY_PADDING;
        if (available >= tableMin + HYSTERESIS) return "push";
        if (available < tableMin - HYSTERESIS) return "overlay";
        return prev; // 임계 ±24px 구간: 직전 모드 유지(히스테리시스)
      };
      const ro = new ResizeObserver((entries) => {
        const w = entries[0].contentRect.width;
        setMode((prev) => decide(w, prev));
      });
      ro.observe(node);
      roRef.current = ro;
    },
    [showDrugImages]
  );

  if (state.phase === "loading") {
    return (
      <Root>
        <CaringNoteStatus variant="loading" />
      </Root>
    );
  }

  if (state.phase === "error" || state.phase === "empty") {
    const kind = state.phase === "empty" ? "empty" : state.kind;
    return (
      <Root>
        <CaringNoteStatus variant="status" kind={kind} onRetry={retry} />
      </Root>
    );
  }

  const { reviewId, content, promptType, isEdited: isEditedRaw, hospitalAliases, patientSnap } =
    state.data;
  const isEdited = isEditedRaw === true;
  // 병원 실명 복원 맵(spec 설계 결정 8 · A안). referenceMode='none'이라 실명 평문으로 복원되며,
  // 맵이 없으면(백엔드 필드 미배포) demask가 실행되지 않고 정리 패스가 ⑆만 벗긴 평문으로 폴백한다.
  const hospitalAliasMap = buildHospitalAliasMapFromRecord(hospitalAliases);

  // patientSnap에서 처방/OTC/건기식 배열을 널·타입 안전하게 꺼낸다(patientSnap은 `any`. 미배포 전이기엔
  // 부재/부분/비배열 가능 — Array.isArray로 감싸 비배열이 와도 .flatMap/.length가 크래시하지 않게 한다).
  const hasPatientSnap = patientSnap != null;
  const prescriptionGroups = Array.isArray(patientSnap?.prescriptionGroups)
    ? patientSnap.prescriptionGroups
    : [];
  const otc = Array.isArray(patientSnap?.otc) ? patientSnap.otc : [];
  const hff = Array.isArray(patientSnap?.hff) ? patientSnap.hff : [];
  const hasPrescriptionData =
    prescriptionGroups.length > 0 || otc.length > 0 || hff.length > 0;

  // 복용약 범위·표시 옵션 데이터 — 약사 인쇄 화면과 같은 유틸 공유(신규 UI 전체 노출, 지니님 확정 2026-09-07).
  // 탑재뷰 content는 편집본이면 이미 편집본 HTML이라 그대로 넣으면 된다(data-itemcode/S3 URL 추출).
  const mentionedItemCodes = extractMentionedItemCodes(content || "");
  const mentionedAdditions = getMentionedAdditions(
    prescriptionGroups,
    mentionedItemCodes,
    new Date()
  );
  const optionAvailability = computeOptionAvailability(
    prescriptionGroups,
    mentionedItemCodes,
    new Date()
  );

  // 본문 약 카드 제품명 복구용 drugsData — 성상(pills) 조인 집합과 같은 범위로 넓힌다(§5.1 · D-18 · P-3·P-4).
  // patientSnap(처방·OTC·건기식·식별의약품)에 pills.itemName 폴백까지 이름 소스로 삼아 "이름 없는 성상 카드"를 막는다.
  const drugsData = [
    ...collectSnapDrugs(patientSnap),
    ...pillsNameFallback(state.data.pills),
  ];
  // 서버가 준 성상 배열을 무가공 그대로 넘긴다(D-4). 매 렌더 새 배열이므로 useEffect deps에 넣지 않는다(§7 리스크).
  const pillsData = state.data.pills ?? [];

  const age = patientSnap?.age ? `${patientSnap.age}세` : "-";

  const shouldRenderPatient = printOptions.showPatientInfo && hasPatientSnap;
  const shouldRenderPrescription =
    printOptions.showPrescriptionTable && hasPrescriptionData;

  // 첫 가시 섹션만 page-break 제외 — 앞 섹션이 숨겨졌을 때 빈 선두 페이지를 막는다(item 6).
  const firstVisibleSection = shouldRenderPatient
    ? "patient"
    : shouldRenderPrescription
    ? "prescription"
    : "analysis";

  const sectionExistence = checkSectionExistence(content);

  // 비활성 사유(§5.5): patientSnap 부재 → 환자 정보 비활성. 처방 데이터 부재 → 처방전 정보·프리셋 비활성.
  const disabledReasons = {
    patientInfo: hasPatientSnap ? undefined : "환자 정보 없음",
    prescriptionTable: hasPrescriptionData ? undefined : "수집된 처방 없음",
  };

  // 우측 예약폭(헤더·본문 양쪽 동일 적용 — 본문에만 걸면 push에서 필수 책임 안내 문구(계약 §6)가 패널
  // 밑으로 잘린다). **레일 폭(48)은 push·overlay 상시 예약**한다 — overlay에서 0으로 두면 본문이 뷰포트
  // 우측 끝까지 차지해 세로 스크롤바가 불투명 레일 밑에 가려진다(classic 스크롤바 환경: Windows·always-show).
  // 레일을 예약하면 스크롤바가 레일 왼쪽에 놓여 보이고 드래그된다. 펼침 시 패널(240)만 push에서 추가로
  // 밀고, overlay에서는 패널이 콘텐츠 위로 떠서(float) 본문 폭이 안 변한다(AC-25).
  const reserveRight =
    mode === "push" ? RAIL_WIDTH + (panelOpen ? PANEL_WIDTH : 0) : RAIL_WIDTH;

  // 인쇄 — PDF 저장 기본 파일명을 `PhAI_보고서{reviewId}`로(브라우저는 document.title을 파일명으로 쓴다).
  // afterprint에서 원복. AIReviewPrintTemplate의 패턴을 참고하되 그 파일은 고치지 않는다(P-15).
  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = `PhAI_보고서${reviewId}`;
    const restore = () => {
      document.title = originalTitle;
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    window.print();
  };

  return (
    <Root className="cn-embed-print" ref={setShellRef}>
      <EmbedPrintStyle />
      <Header $reserveRight={reserveRight}>
        <Notice>
          <Text element="div" textType="Caption" color={COLOR_SYSTEM.get("Gray")?.[500]}>
            본 복약지도문은 AI가 생성한 초안입니다. 내용을 확인·수정 후 활용하시기 바라며,
            초안을 그대로 사용하는 경우의 책임은 사용자에게 있습니다.
          </Text>
        </Notice>
      </Header>
      <BodyWrap>
      <ScrollBody ref={bodyRef} $reserveRight={reserveRight}>
        <Inner>
          {shouldRenderPatient && (
            <Section data-print-anchor="patient" $pageBreak={firstVisibleSection !== "patient"}>
              <SectionTitle>환자 정보</SectionTitle>
              <PatientInfoDisplay
                patient={patientSnap}
                age={age}
                promptType={promptType ?? undefined}
                hidePatientNo
              />
            </Section>
          )}

          {shouldRenderPrescription && (
            <Section
              data-print-anchor="prescription"
              $pageBreak={firstVisibleSection !== "prescription"}
            >
              <SectionTitle>복용약 정보</SectionTitle>
              {/* 범위 필터·언급 처방전 병합·캡션·소표·배지 범례 — 약사 인쇄 화면과 공유 조립 */}
              <PrescriptionScopeSection
                prescriptionGroups={prescriptionGroups}
                otc={otc}
                hff={hff}
                printOptions={printOptions}
                drugsData={drugsData}
                mentionedAdditions={mentionedAdditions}
              />
            </Section>
          )}

          {/* 3단·독립뷰와 같은 공유 렌더러. 읽기전용 배선(reviewId·onDrugImageClick 미전달,
              referenceMode='none', isEditMode=false, FeedbackProvider 부재).
              본문 약 카드 제품명은 patientSnap+pills로 넓힌 drugsData로 복구하고(§5.1 · D-18),
              서버가 준 pills를 무가공 그대로 pillsData로 넘겨 성상·각인을 3단 독립뷰와 같은 값으로 채운다(D-4).
              편집본(isEdited)은 3단과 동일하게 HtmlViewer로, 아니면 MarkdownViewer로 간다(§5.2, 현행 배선 유지).
              병원 마커는 hospitalAliasMap으로 실명 평문 복원(spec 설계 결정 8 · A안). onHospitalClick을
              넘기지 않으므로 클릭 비활성 평문 경로다(좌측 패널이 없는 탑재뷰라 하이라이트 배선도 없다). */}
          <Section data-print-anchor="analysis" $pageBreak={firstVisibleSection !== "analysis"}>
            <AnalysisCardScope $cardSize={printOptions.patientGuideCardSize}>
            <ReviewSection
              reviewResult={content}
              reviewResultEdited={isEdited ? content : undefined}
              status={AIReviewStatus.COMPLETED}
              message=""
              promptType={promptType ?? PromptType.MEDICATION_COUNSELING}
              isEditMode={false}
              editedContent=""
              showEditedVersion={isEdited}
              onEnterEditMode={noop}
              onExitEditMode={noop}
              onContentChange={noop}
              onSave={noop}
              onDelete={noop}
              onReferenceClick={noop}
              referenceMode="none"
              drugsData={drugsData}
              pillsData={pillsData}
              sectionVisibility={{
                hidePharmacistReport: printOptions.hidePharmacistReport,
                hidePatientGuide: printOptions.hidePatientGuide,
              }}
              hospitalAliasMap={hospitalAliasMap}
            />
            </AnalysisCardScope>
          </Section>
        </Inner>
      </ScrollBody>
      {changePill && (
        <ChangePill
          $direction={changePill.direction}
          // 우측 도킹 패널/레일 예약폭만큼 본문 중앙을 보정한다(BodyWrap은 예약폭을 모른다)
          style={{ left: `calc(50% - ${reserveRight / 2}px)` }}
          onClick={handlePillClick}
        >
          {changePill.direction === "up"
            ? "↑ 위쪽 내용이 변경되었습니다"
            : "↓ 아래쪽 내용이 변경되었습니다"}
        </ChangePill>
      )}
      </BodyWrap>
      <PrintSidePanel
        collapsible
        defaultOpen={initialOpen}
        onToggle={handleToggle}
        printOptions={printOptions}
        onOptionChange={handleOptionChange}
        onPrint={handlePrint}
        sectionExistence={sectionExistence}
        disabledReasons={disabledReasons}
        showDrugScope
        mentionedAdditions={mentionedAdditions}
        optionAvailability={optionAvailability}
      />
    </Root>
  );
};

export default CaringNoteEmbedPage;
